/**
 * Linked Bank Accounts API Client
 * Handles requests to linked bank account endpoints
 */

import { z } from 'zod';
import {
  LinkedBankAccountResponseSchema,
  CreateLinkedBankAccountRequestSchema,
} from '@resida/api-types/linked-bank-accounts';

const API_BASE = '/landlord/api/manager';

export interface LinkTokenResponse {
  linkToken: string;
  expiration: string;
}

// Derived from LinkedBankAccountResponseSchema in @resida/api-types/linked-bank-accounts
export type LinkedBankAccount = z.infer<typeof LinkedBankAccountResponseSchema>;

export interface LinkedBankAccountListResponse {
  accounts: LinkedBankAccount[];
}

// Derived from CreateLinkedBankAccountRequestSchema in @resida/api-types/linked-bank-accounts
export type CreateLinkedBankAccountRequest = z.infer<typeof CreateLinkedBankAccountRequestSchema>;

export interface SyncResult {
  added: number;
  modified: number;
  removed: number;
}

export async function createLinkToken(): Promise<LinkTokenResponse> {
  const response = await fetch(`${API_BASE}/linked-bank-accounts/link-token`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (!response.ok) throw new Error(`Failed to create link token: ${response.status}`);
  return response.json();
}

export async function exchangePublicToken(
  data: CreateLinkedBankAccountRequest
): Promise<LinkedBankAccount> {
  const response = await fetch(`${API_BASE}/linked-bank-accounts`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(`Failed to link bank account: ${response.status}`);
  return response.json();
}

export async function fetchLinkedBankAccounts(): Promise<LinkedBankAccountListResponse> {
  const response = await fetch(`${API_BASE}/linked-bank-accounts`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Failed to fetch linked bank accounts: ${response.status}`);
  return response.json();
}

export async function unlinkBankAccount(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/linked-bank-accounts/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Failed to unlink bank account: ${response.status}`);
}

export async function syncBankAccount(id: string): Promise<SyncResult> {
  const response = await fetch(`${API_BASE}/linked-bank-accounts/${id}/sync`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Failed to sync bank account: ${response.status}`);
  return response.json();
}
